import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { navigateToConsultation } from '../utils/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  FiArrowRight, FiTarget, FiUsers, FiStar, FiTrendingDown, 
  FiTrendingUp, FiHeart, FiAward, FiClock, FiBook, FiActivity, FiSun,
  FiChevronLeft, FiChevronRight, FiMessageCircle, FiImage, FiUsers as FiUsersIcon,
  FiPlay, FiX, FiCheck
} from 'react-icons/fi';
import { FaCalculator } from 'react-icons/fa';
import { getRecipes, getReviews, getPresignedUrl } from '../utils/api';
import { getRecipeImageUrl, getReviewImageUrl } from '../utils/imageUtils';
import { getVideoInfo } from '../utils/helpers';
import BMICalculator from '../components/BMICalculator';
import Counter from '../components/Counter';
import YouTubeShorts from '../components/YouTubeShorts';
import YouTubeShortsCarousel from '../components/YouTubeShortsCarousel';

const Home = () => {
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [imageErrors, setImageErrors] = useState({});
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [founderRef, founderInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [reviewsRef, reviewsInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [recipesRef, recipesInView] = useInView({ threshold: 0.1, triggerOnce: true });

  const [founderImageError, setFounderImageError] = useState(false);
  const [founderImageUrl, setFounderImageUrl] = useState(null);
  const [founderImageLoading, setFounderImageLoading] = useState(false);

  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'HealthAndBeautyBusiness',
    'name': 'Dfitclub by Deeksha Rajput',
    'image': 'https://dfitclub.com/favicon.png',
    '@id': 'https://dfitclub.com/#organization',
    'url': 'https://dfitclub.com',
    'telephone': '+91-9355353459',
    'priceRange': '$$',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'IN'
    },
    'description': 'Transform your health with personalized weight loss, weight gain, and clinical disease management programs by clinical nutritionist Deeksha Rajput.',
    'founder': {
      '@type': 'Person',
      'name': 'Deeksha Rajput',
      'jobTitle': 'Certified Expert & Clinical Nutritionist'
    },
    'sameAs': [
      'https://www.instagram.com/d_fitclub_/',
      'https://www.facebook.com/share/1Axj8uhKsP/?mibextid=wwXIfr',
      'https://www.youtube.com/@fit_with_Deeksha'
    ]
  };

  useSEO({
    title: 'Dfitclub - Health & Nutrition Transformation',
    description: 'Transform your health with personalized weight loss and weight gain programs by certified nutritionist Deeksha Rajput. Custom meal plans, WhatsApp support, and proven results.',
    keywords: 'weight loss, weight gain, diet plans, personalized nutrition, PCOS management, Deeksha Rajput, Dfitclub',
    schema: homeSchema
  });

  const getYouTubeThumbnail = (videoId) => {
    return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  };

  const getEmbedUrl = (recipe) => {
    if (!recipe || !recipe.youtube_video_id) return '';
    const videoInfo = getVideoInfo(recipe.youtube_video_id);
    if (!videoInfo) return '';

    if (videoInfo.provider === 'instagram') {
      return `https://www.instagram.com/p/${videoInfo.id}/embed/`;
    }
    if (videoInfo.provider === 'facebook') {
      return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(videoInfo.id)}&show_text=0&t=0`;
    }
    return `https://www.youtube.com/embed/${videoInfo.id}?autoplay=1&mute=1&controls=1&rel=0&fs=1&enablejsapi=1`;
  };

  const parseIngredients = (ingredientsString) => {
    try {
      return JSON.parse(ingredientsString);
    } catch {
      if (typeof ingredientsString === 'string') {
        return ingredientsString.split('\n').filter(item => item.trim());
      }
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('🔄 Fetching data...');
        
        // Try to fetch data with better error handling
        let recipesData = null;
        let reviewsData = null;
        
        try {
          recipesData = await getRecipes({ featured: true });
          console.log('🍳 Recipes data:', recipesData);
        } catch (error) {
          console.error('❌ Failed to fetch recipes:', error);
        }
        
        try {
          reviewsData = await getReviews({ featured: true });
          console.log('⭐ Reviews data:', reviewsData);
        } catch (error) {
          console.error('❌ Failed to fetch reviews:', error);
        }
        
        // Set data with fallbacks
        setRecipes(recipesData?.recipes?.slice(0, 4) || []);
        setReviews(reviewsData?.reviews?.slice(0, 9) || []);
        
        console.log('✅ Data set - Recipes:', recipesData?.recipes?.slice(0, 4) || []);
        console.log('✅ Data set - Reviews:', reviewsData?.reviews?.slice(0, 9) || []);
      } catch (error) {
        console.error('❌ Failed to fetch data:', error);
        // Set empty arrays as fallback
        setRecipes([]);
        setReviews([]);
      } finally {
        setLoading(false);
        setReviewsLoading(false);
      }
    };

    fetchData();
  }, []);

  const founderImageEnv = process.env.REACT_APP_FOUNDER_IMAGE_URL || process.env.REACT_APP_FOUNDER_IMAGE_KEY || null;

  useEffect(() => {
    const fetchFounderPresignedUrl = async () => {
      if (!founderImageEnv) return;
      setFounderImageLoading(true);
      try {
        if (founderImageEnv.startsWith('http://') || founderImageEnv.startsWith('https://')) {
          if (founderImageEnv.includes('s3.amazonaws.com')) {
            const s3Key = founderImageEnv.split('.amazonaws.com/')[1];
            const presignedUrl = await getPresignedUrl(s3Key);
            setFounderImageUrl(presignedUrl);
          } else {
            setFounderImageUrl(founderImageEnv);
          }
        } else if (founderImageEnv.startsWith('s3://')) {
          const s3Key = founderImageEnv.replace(/^s3:\/\/[^\/]+\//, '');
          const presignedUrl = await getPresignedUrl(s3Key);
          setFounderImageUrl(presignedUrl);
        } else {
          const presignedUrl = await getPresignedUrl(founderImageEnv);
          setFounderImageUrl(presignedUrl);
        }
      } catch (error) {
        console.error('Failed to get presigned URL for founder image:', error);
        setFounderImageUrl(founderImageEnv);
      } finally {
        setFounderImageLoading(false);
      }
    };
    fetchFounderPresignedUrl();
  }, [founderImageEnv]);



  const getCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1440) return 3; // Large desktop
      if (window.innerWidth >= 1024) return 3; // Desktop
      if (window.innerWidth >= 768) return 2;  // Tablet
      return 1; // Mobile
    }
    return 3; // Default
  };


  const stats = [
    { icon: FiUsers, value: 100, suffix: '+', label: 'Happy Clients' },
    { icon: FiTarget, value: 95, suffix: '%', label: 'Success Rate' },
    { icon: FiAward, value: 5, suffix: '+', label: 'Years Experience' },
    { icon: FiStar, value: 4.8, suffix: '', label: 'Average Rating' }
  ];

  const services = [
    {
      icon: FiTrendingDown,
      title: 'Weight Loss Programs',
      description: 'Sustainable weight loss with personalized diet plans and guidance',
      features: ['Custom meal plans', '24/7 support', 'Progress tracking'],
      to: '/services#weight-loss'
    },
    {
      icon: FiTrendingUp,
      title: 'Weight Gain Programs',
      description: 'Healthy weight gain through structured nutrition and lifestyle changes',
      features: ['Muscle building diet', 'Workout guidance', 'Regular monitoring'],
      to: '/services#weight-gain'
    },
    {
      icon: FiActivity,
      title: 'Disease Management',
      description: 'Specialized nutrition for PCOS/PCOD, Thyroid, and Diabetes management',
      features: ['Condition-specific plans', 'Health monitoring', 'Lifestyle modifications'],
      to: '/disease-management'
    },
    {
      icon: FiSun,
      title: 'Skin Toning',
      description: 'Nutrition-focused approach for healthy, glowing skin from within',
      features: ['Skin-specific nutrition', 'Detox protocols', 'Anti-aging foods'],
      to: '/services#skin-toning'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Clean Centered Design */}
      <section id="hero" ref={heroRef} className="hero-gradient pt-8 pb-12 md:pt-10 md:pb-16 lg:pt-12 lg:pb-20 overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-primary-100 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent-100 rounded-full blur-3xl"></div>
        </div>

        <div className="container-max relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8 }}
              className="space-y-8"
            >
                                  <div className="space-y-4">
                      <motion.div
                        className="welcome-container flex justify-center"
                        initial={{ opacity: 0, y: 20 }}
                        animate={heroInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.1 }}
                      >
                        <motion.span 
                          className="inline-flex items-center px-4 py-2 bg-orange-100 text-orange-700 rounded-full text-sm font-medium"
                          initial={{ opacity: 0, y: 10 }}
                          animate={heroInView ? { opacity: 1, y: 0 } : {}}
                          transition={{ duration: 0.8, delay: 0.3 }}
                        >
                          ✨ Transform Your Health with Expert Guidance
                        </motion.span>
                      </motion.div>
                      <motion.h1 
                        className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 leading-tight"
                        initial={{ opacity: 0, y: 20 }}
                        animate={heroInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      >
                        Your Health Transformation
                        <span className="text-visible-orange block">Starts Here</span>
                      </motion.h1>
                      <motion.p 
                        className="text-base text-gray-600 max-w-2xl mx-auto leading-relaxed"
                        initial={{ opacity: 0, y: 20 }}
                        animate={heroInView ? { opacity: 1, y: 0 } : {}}
                        transition={{ duration: 0.8, delay: 0.4 }}
                      >
                        Join thousands who've transformed their lives with personalized nutrition plans, expert guidance, and proven wellness strategies from certified nutritionist Deeksha Rajput.
                      </motion.p>
                    </div>

              <motion.div 
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Link 
                  to="/contact#consultation" 
                  className="btn-primary flex items-center justify-center space-x-2"
                  onClick={() => {
                    console.log('🔍 Hero consultation button clicked - using direct link');
                  }}
                >
                  <span>Get Your Personalized Nutrition Plan</span>
                  <FiArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/transformations" className="btn-secondary flex items-center justify-center space-x-2">
                  <span>View Success Stories</span>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 max-w-lg mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={heroInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-3xl md:text-4xl font-extrabold text-visible-orange mb-1">
                      <Counter end={stat.value} suffix={stat.suffix} duration={2500} />
                    </div>
                    <div className="text-xs text-gray-600">{stat.label}</div>
                  </div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>


      {/* Meet Deeksha Rajput Section */}
      <section ref={founderRef} className="section-padding bg-white">
        <div className="container-max">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Image side */}
            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, x: -30 }}
              animate={founderInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8 }}
            >
              {founderImageLoading ? (
                <div className="w-full aspect-[3/4] max-w-sm mx-auto bg-gradient-to-br from-orange-100 to-orange-200 rounded-3xl flex items-center justify-center">
                  <div className="text-center text-orange-850">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-850 mx-auto mb-2"></div>
                    <p className="text-xs">Loading...</p>
                  </div>
                </div>
              ) : founderImageUrl && !founderImageError ? (
                <div className="w-full aspect-[3/4] max-w-sm mx-auto rounded-3xl overflow-hidden shadow-xl ring-4 ring-orange-55 relative">
                  <img 
                    src={founderImageUrl} 
                    alt="Deeksha Rajput"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                    onError={() => setFounderImageError(true)}
                  />
                </div>
              ) : (
                <div className="w-full aspect-[3/4] max-w-sm mx-auto bg-gradient-to-br from-orange-100 to-orange-200 rounded-3xl flex items-center justify-center">
                  <div className="text-center text-orange-850">
                    <FiAward className="w-16 h-16 mx-auto mb-2" />
                    <p className="text-base font-semibold">Certified Expert</p>
                    <p className="text-xs">Clinical Nutritionist</p>
                  </div>
                </div>
              )}
            </motion.div>

            {/* Text side */}
            <motion.div
              className="lg:col-span-7 space-y-6"
              initial={{ opacity: 0, x: 30 }}
              animate={founderInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div>
                <span className="text-visible-orange font-semibold tracking-wider uppercase text-xs block mb-1">Founder & Lead Dietitian</span>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 leading-tight">
                  Meet <span className="text-visible-orange">Deeksha Rajput</span>
                </h2>
              </div>
              
              <p className="text-secondary-600 text-sm leading-relaxed">
                With over 5 years of clinical experience and a passion for transforming lives, Deeksha founded Dfitclub to bridge the gap between scientific nutrition knowledge and practical, sustainable lifestyle changes.
              </p>
              <p className="text-secondary-600 text-sm leading-relaxed">
                Her approach combines evidence-based nutrition science with personalized care, ensuring that each client receives the individual attention they deserve on their health journey.
              </p>

              {/* Qualifications preview */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center space-x-2">
                  <FiCheck className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-secondary-700 text-xs font-medium">Certified Nutritionist & Dietitian</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheck className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-secondary-700 text-xs font-medium">Masters in Clinical Nutrition</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheck className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-secondary-700 text-xs font-medium">Specialized in Weight Management</span>
                </div>
                <div className="flex items-center space-x-2">
                  <FiCheck className="w-4 h-4 text-orange-500 flex-shrink-0" />
                  <span className="text-secondary-700 text-xs font-medium">Certified Lifestyle Coach</span>
                </div>
              </div>

              <div className="pt-4">
                <Link to="/about" className="btn-secondary inline-block">
                  Read My Full Story
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section id="reviews" ref={reviewsRef} className="section-padding bg-gray-50">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={reviewsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
              Client <span className="text-visible-orange">Reviews</span>
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              See what our clients have to say about their transformation journey
            </p>
          </motion.div>

          {reviewsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-48 bg-gray-200 rounded-t-xl"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 text-lg">No reviews found</div>
            </div>
          ) : reviews.length <= getCardsPerView() ? (
            /* Simple Grid for small number of items */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-center">
              {reviews.map((review, index) => (
                <div key={review.id} className="w-full max-w-sm mx-auto">
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.1 }}
                    className="card card-hover overflow-hidden flex flex-col h-full"
                  >


                    {/* Image First (if present) */}
                    {review.image && (
                      <div className="w-full aspect-video max-h-40 overflow-hidden relative bg-gray-100 flex-shrink-0">
                        <img 
                          src={getReviewImageUrl(review.image)} 
                          alt="Review" 
                          className="w-full h-full object-cover object-center"
                          style={{ minHeight: '0', maxHeight: '160px' }}
                          onError={(e) => {
                            console.error('❌ Failed to load review image:', review.image);
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-5 flex-1 flex flex-col">
                      {/* Rating */}
                      <div className="flex items-center text-amber-500 mb-3">
                        {[...Array(review.rating || 5)].map((_, i) => (
                          <FiStar key={i} className="w-5 h-5 fill-current" />
                        ))}
                      </div>

                      {/* Review Content */}
                      <p className="text-secondary-700 mb-3 text-sm leading-relaxed flex-1">
                        "{review.content}"
                      </p>

                      {/* Author */}
                      <div className="mt-auto pt-4 border-t border-gray-200">
                        <h4 className="font-semibold text-secondary-900">{review.author}</h4>
                      </div>
                    </div>
                  </motion.div>
                </div>
              ))}
            </div>
          ) : (
            <div className="relative">
              {/* Continuous Carousel */}
              <div className="continuous-carousel">
                <div
                  className="carousel-track continuous-scroll"
                  style={{
                    width: `${((reviews.length * 2) / getCardsPerView()) * 100}%`
                  }}
                >
                  {/* Duplicate reviews for continuous effect */}
                  {[...reviews, ...reviews].map((review, index) => (
                    <div
                      key={`${review.id}-${index}`}
                      className="carousel-item px-4"
                      style={{ width: `${100 / (reviews.length * 2)}%` }}
                    >
                      <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: index * 0.1 }}
                        className="card card-hover overflow-hidden flex flex-col h-full"
                      >


                        {/* Image First (if present) */}
                        {review.image && (
                          <div className="w-full aspect-video max-h-40 overflow-hidden relative bg-gray-100 flex-shrink-0">
                            <img 
                              src={getReviewImageUrl(review.image)} 
                              alt="Review" 
                              className="w-full h-full object-cover object-center"
                              style={{ minHeight: '0', maxHeight: '160px' }}
                              onError={(e) => {
                                console.error('❌ Failed to load review image:', review.image);
                                e.target.style.display = 'none';
                              }}
                            />
                          </div>
                        )}

                        {/* Content */}
                        <div className="p-5 flex-1 flex flex-col">
                          {/* Rating */}
                          <div className="flex items-center text-amber-500 mb-3">
                            {[...Array(review.rating || 5)].map((_, i) => (
                              <FiStar key={i} className="w-5 h-5 fill-current" />
                            ))}
                          </div>

                          {/* Review Content */}
                          <p className="text-secondary-700 mb-3 text-sm leading-relaxed flex-1">
                            "{review.content}"
                          </p>

                          {/* Author */}
                          <div className="mt-auto pt-4 border-t border-gray-200">
                            <h4 className="font-semibold text-secondary-900">{review.author}</h4>
                          </div>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={reviewsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-12"
          >
            <Link to="/transformations" className="btn-primary">
              View All Reviews
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" ref={servicesRef} className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
              Our <span className="text-visible-orange">Specialized Programs</span>
            </h2>
            <p className="text-base md:text-xl text-secondary-600 max-w-3xl mx-auto">
              Comprehensive health solutions tailored to your unique goals and lifestyle
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50, scale: 0.9 }}
                animate={servicesInView ? { 
                  opacity: 1, 
                  y: 0, 
                  scale: 1,
                  rotateY: [0, 5, 0],
                  rotateX: [0, -2, 0]
                } : {}}
                transition={{ 
                  duration: 0.8, 
                  delay: index * 0.15,
                  type: "spring",
                  stiffness: 100,
                  damping: 15
                }}
                whileHover={{
                  y: -8,
                  scale: 1.02,
                  rotateY: 8,
                  rotateX: -3,
                  transition: { duration: 0.3, ease: "easeOut" }
                }}
                className="relative card card-hover p-4 text-center"
              >
                {/* Floating animation background */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-br from-primary-50 to-secondary-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  animate={{
                    y: [0, -5, 0],
                    x: [0, 3, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    delay: index * 0.5,
                    ease: "easeInOut"
                  }}
                />
                
                {/* Glow effect */}
                <motion.div
                  className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: `radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.1) 0%, transparent 70%)`
                  }}
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />

                {/* Icon with enhanced animation */}
                <motion.div 
                  className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-3 relative z-10"
                  whileHover={{
                    scale: 1.1,
                    rotate: 5,
                    transition: { duration: 0.3 }
                  }}
                  animate={{
                    y: [0, -3, 0],
                    rotate: [0, 1, 0]
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    delay: index * 0.3,
                    ease: "easeInOut"
                  }}
                >
                  <motion.div
                    className="absolute inset-0 rounded-2xl bg-gradient-to-br from-primary-300 to-primary-400 opacity-0 group-hover:opacity-20 transition-opacity duration-300"
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0, 0.3, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />
                  <service.icon className="w-6 h-6 text-primary-600 relative z-10" />
                </motion.div>

                {/* Content with staggered animation */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
                  className="relative z-10"
                >
                  <h3 className="text-lg font-semibold text-secondary-900 mb-1 group-hover:text-primary-700 transition-colors duration-300">
                    {service.title}
                  </h3>
                  <p className="text-sm text-secondary-600 mb-3 group-hover:text-secondary-700 transition-colors duration-300 line-clamp-2">
                    {service.description}
                  </p>
                  
                  {/* Features with staggered animation - Hidden on mobile */}
                  <ul className="hidden md:block space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <motion.li 
                        key={featureIndex} 
                        className="flex items-center justify-center space-x-2"
                        initial={{ opacity: 0, x: -10 }}
                        animate={servicesInView ? { opacity: 1, x: 0 } : {}}
                        transition={{ 
                          duration: 0.4, 
                          delay: index * 0.2 + 0.4 + featureIndex * 0.1 
                        }}
                        whileHover={{ x: 5, transition: { duration: 0.2 } }}
                      >
                        <motion.div 
                          className="w-2 h-2 bg-primary-500 rounded-full"
                          animate={{
                            scale: [1, 1.2, 1],
                            opacity: [0.7, 1, 0.7]
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                            delay: featureIndex * 0.2
                          }}
                        />
                        <span className="text-sm text-secondary-600 group-hover:text-secondary-700 transition-colors duration-300">
                          {feature}
                        </span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* Enhanced Learn More link */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Link 
                      to={service.to} 
                      className="inline-flex items-center space-x-2 text-primary-600 font-medium hover:text-primary-700 transition-all duration-300 group-hover:scale-105 text-sm md:text-base"
                    >
                      <span>Learn More</span>
                      <motion.div
                        animate={{ x: [0, 3, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      >
                        <FiArrowRight className="w-4 h-4" />
                      </motion.div>
                    </Link>
                  </motion.div>
                </motion.div>

                {/* Particle effects - Hidden on mobile */}
                <motion.div
                  className="absolute top-4 right-4 w-2 h-2 bg-primary-400 rounded-full opacity-0 group-hover:opacity-100 hidden md:block"
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    y: [0, -20, -40]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                />
                <motion.div
                  className="absolute bottom-4 left-4 w-1 h-1 bg-secondary-400 rounded-full opacity-0 group-hover:opacity-100 hidden md:block"
                  animate={{
                    scale: [0, 1, 0],
                    opacity: [0, 1, 0],
                    x: [0, 15, 30]
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    delay: 1
                  }}
                />
                <Link
                  to={service.to}
                  className="absolute inset-0 z-10"
                />
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-12"
          >
            <Link to="/services" className="btn-primary">
              View All Services
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Healthy Recipes Section */}
      <section id="recipes" ref={recipesRef} className="section-padding bg-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={recipesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-3">
              Healthy & Taste-Packed <span className="text-visible-orange">Recipes</span>
            </h2>
            <p className="text-base text-gray-600 max-w-2xl mx-auto">
              Delicious, dietitian-approved meal prep ideas and nutrition hacks to support your wellness journey.
            </p>
          </motion.div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 h-96 animate-pulse">
                  <div className="h-52 bg-gray-200" />
                  <div className="p-5 space-y-3">
                    <div className="h-6 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-5/6" />
                  </div>
                </div>
              ))}
            </div>
          ) : recipes.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500 text-lg">No recipes found</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {recipes.slice(0, 4).map((recipe, index) => {
                const videoInfo = recipe.youtube_video_id ? getVideoInfo(recipe.youtube_video_id) : null;
                const hasUploadedImage = recipe.image_url || recipe.image;
                
                // Fallback thumbnail resolve
                let cardImage = null;
                if (hasUploadedImage) {
                  cardImage = getRecipeImageUrl(recipe.image_url || recipe.image);
                } else if (videoInfo) {
                  if (videoInfo.provider === 'youtube') {
                    cardImage = getYouTubeThumbnail(videoInfo.id);
                  } else if (videoInfo.provider === 'instagram') {
                    cardImage = `https://www.instagram.com/p/${videoInfo.id}/media/?size=l`;
                  }
                }

                const hasImageError = imageErrors[recipe.id];

                return (
                  <motion.div
                    key={recipe.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={recipesInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -8, transition: { duration: 0.3 } }}
                    onClick={() => setSelectedRecipe(recipe)}
                    className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 transition-all duration-300 flex flex-col h-full cursor-pointer group"
                  >
                    {/* Image Header */}
                    <div className="relative h-52 overflow-hidden bg-orange-50">
                      {cardImage && !hasImageError ? (
                        <motion.img
                          src={cardImage}
                          alt={recipe.title}
                          onError={() => setImageErrors(prev => ({ ...prev, [recipe.id]: true }))}
                          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-orange-50 to-amber-100 text-orange-500">
                          🥗
                        </div>
                      )}

                      {/* Play Button Overlay */}
                      {recipe.youtube_video_id && (
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="w-12 h-12 rounded-full bg-orange-500 text-white flex items-center justify-center shadow-lg transform scale-90 group-hover:scale-100 transition-transform duration-300">
                            <FiPlay className="w-5 h-5 ml-0.5 fill-current" />
                          </div>
                        </div>
                      )}

                    </div>

                    {/* Card Body */}
                    <div className="p-5 flex flex-col flex-grow">
                      {/* Badges */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        <span className="px-2 py-0.5 bg-orange-100 text-orange-800 text-[10px] font-bold uppercase rounded-full tracking-wider">
                          {recipe.meal_type.replace('_', ' ')}
                        </span>
                        {recipe.goal_type && (
                          <span className={`px-2 py-0.5 text-[10px] font-bold uppercase rounded-full tracking-wider ${
                            recipe.goal_type === 'weight_loss'
                              ? 'bg-blue-100 text-blue-800'
                              : recipe.goal_type === 'weight_gain'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-purple-100 text-purple-800'
                          }`}>
                            {recipe.goal_type === 'weight_loss' ? 'Weight Loss' : 
                             recipe.goal_type === 'weight_gain' ? 'Weight Gain' : 'Wellness'}
                          </span>
                        )}
                      </div>

                      <h3 className="text-base font-bold text-gray-900 line-clamp-1 mb-2 group-hover:text-orange-500 transition-colors">
                        {recipe.title}
                      </h3>
                      
                      <p className="text-secondary-600 text-sm line-clamp-2 mb-4 leading-relaxed flex-grow">
                        {recipe.description || 'View details, cooking instructions, and ingredient breakdown for this healthy recipe.'}
                      </p>

                      {/* Metrics bar */}
                      <div className="flex items-center justify-between text-xs text-secondary-500 pt-3 border-t border-gray-100 font-medium">
                        <span className="flex items-center gap-1">
                          <FiClock className="w-3.5 h-3.5 text-gray-400" />
                          {recipe.total_time || (parseInt(recipe.prep_time || 0) + parseInt(recipe.cook_time || 0)) || 15} mins
                        </span>
                        <span className="flex items-center gap-1">
                          <FiUsers className="w-3.5 h-3.5 text-gray-400" />
                          {recipe.servings || 1} Servings
                        </span>
                        {recipe.calories_per_serving && (
                          <span className="px-2 py-0.5 bg-orange-50 text-orange-700 font-bold rounded">
                            {recipe.calories_per_serving} kcal
                          </span>
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={recipesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center mt-12"
          >
            <Link to="/recipes" className="btn-primary">
              View All Recipes
            </Link>
          </motion.div>
        </div>
      </section>

      {/* BMI Calculator Section */}
      <section id="bmi-calculator" className="section-padding bg-gradient-to-br from-neutral-50 to-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-8"
          >
            <div className="flex items-center justify-center mb-2">
              <FaCalculator className="w-6 h-6 text-orange-500 mr-2" />
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide">
                Free Tool
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-2">
              Know Your <span className="text-visible-orange">Health Status</span>
            </h2>
            <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
              Calculate your BMI instantly and get personalized recommendations for your health journey
            </p>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <BMICalculator />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-100 to-primary-200 text-neutral-800">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Start Your Transformation?
            </h2>
            <p className="text-base text-gray-600 leading-relaxed mb-6">
              Join thousands of successful clients who have achieved their health goals with personalized guidance from Deeksha Rajput.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/contact#consultation" 
                className="btn-accent"
                onClick={() => {
                  console.log('🔍 CTA consultation button clicked - using direct link');
                }}
              >
                Enroll Now
              </Link>
              <Link to="/about" className="btn-secondary">
                Learn About Deeksha
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Immersive Recipe Details Modal */}
      <AnimatePresence>
        {selectedRecipe && (() => {
          const videoInfo = selectedRecipe.youtube_video_id ? getVideoInfo(selectedRecipe.youtube_video_id) : null;
          const isInstagram = videoInfo && videoInfo.provider === 'instagram';
          const isFacebook = videoInfo && videoInfo.provider === 'facebook';
          const isVertical = isInstagram || isFacebook;
          
          return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              {/* Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setSelectedRecipe(null)}
                className="absolute inset-0 bg-neutral-900/80 backdrop-blur-md"
              />

              {/* Modal Body */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="bg-white rounded-3xl overflow-hidden shadow-2xl max-w-5xl w-full max-h-[90vh] md:max-h-[85vh] flex flex-col md:flex-row relative z-10"
              >
                {/* Close Button */}
                <button
                  onClick={() => setSelectedRecipe(null)}
                  className="absolute top-4 right-4 z-50 p-2.5 rounded-full bg-white/90 backdrop-blur-sm text-neutral-800 hover:bg-orange-500 hover:text-white shadow transition-all duration-200"
                >
                  <FiX className="w-5 h-5" />
                </button>

                {/* Left Column: Video Embed / Cover Image */}
                <div className="w-full md:w-[55%] bg-neutral-950 flex items-center justify-center relative aspect-video md:aspect-auto min-h-[350px] md:min-h-[500px] p-4">
                  {selectedRecipe.youtube_video_id ? (
                    isVertical ? (
                      /* Styled centered vertical container for Instagram Reels & Facebook Reels */
                      <div className="w-full max-w-[310px] h-full max-h-[500px] aspect-[9/16] bg-neutral-900 rounded-2xl overflow-hidden shadow-2xl relative border border-neutral-800 flex items-center justify-center">
                        <iframe
                          src={getEmbedUrl(selectedRecipe)}
                          title={selectedRecipe.title}
                          className="w-full h-full absolute inset-0"
                          frameBorder="0"
                          scrolling="no"
                          allowTransparency="true"
                          allow="autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ border: 'none', overflow: 'hidden' }}
                        />
                      </div>
                    ) : (
                      /* Full landscape view for YouTube */
                      <iframe
                        src={getEmbedUrl(selectedRecipe)}
                        title={selectedRecipe.title}
                        className="w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      />
                    )
                  ) : (
                    <img
                      src={selectedRecipe.image_url || selectedRecipe.image ? getRecipeImageUrl(selectedRecipe.image_url || selectedRecipe.image) : ''}
                      alt={selectedRecipe.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                </div>

                {/* Right Column: Recipe Details */}
                <div className="w-full md:w-[45%] p-6 md:p-8 overflow-y-auto flex flex-col h-full max-h-[50vh] md:max-h-[85vh] scrollbar-thin">
                  <div className="flex gap-2 mb-3">
                    <span className="px-2.5 py-1 bg-orange-100 text-orange-800 text-xs font-bold rounded-full uppercase tracking-wider">
                      {selectedRecipe.meal_type.replace('_', ' ')}
                    </span>
                    {selectedRecipe.goal_type && (
                      <span className="px-2.5 py-1 bg-orange-500 text-white text-xs font-bold rounded-full uppercase tracking-wider">
                        {selectedRecipe.goal_type.replace('_', ' ')}
                      </span>
                    )}
                  </div>

                  <h2 className="text-2xl md:text-3xl font-extrabold text-neutral-900 leading-tight mb-3">
                    {selectedRecipe.title}
                  </h2>

                  <p className="text-neutral-600 text-sm leading-relaxed mb-6">
                    {selectedRecipe.description || 'This healthy dish fits perfectly into a well-balanced training or weight control regimen.'}
                  </p>

                  {/* Info Pills */}
                  <div className="grid grid-cols-4 gap-3 text-center border-t border-b border-gray-100 py-4 mb-6 bg-neutral-50/50 rounded-xl p-3">
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Prep</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.prep_time || 5}m</div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Cook</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.cook_time || 10}m</div>
                    </div>
                    <div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mb-1 font-semibold">Servings</div>
                      <div className="font-bold text-neutral-900">{selectedRecipe.servings || 1}</div>
                    </div>
                    <div>
                      <div className="text-xs text-orange-500 uppercase tracking-wider mb-1 font-bold">Calories</div>
                      <div className="font-extrabold text-orange-600">{selectedRecipe.calories_per_serving || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Ingredients */}
                  <div className="mb-6">
                    <h4 className="text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">🛒</span> Ingredients
                    </h4>
                    <ul className="space-y-2">
                      {parseIngredients(selectedRecipe.ingredients).map((ing, idx) => (
                        <li key={idx} className="text-sm text-neutral-700 flex items-start">
                          <span className="w-1.5 h-1.5 bg-orange-500 rounded-full mt-2 mr-2.5 flex-shrink-0" />
                          <span>{ing}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Instructions */}
                  <div>
                    <h4 className="text-lg font-bold text-neutral-900 mb-3 flex items-center gap-2">
                      <span className="text-xl">📝</span> Instructions
                    </h4>
                    <p className="text-sm text-neutral-700 leading-relaxed whitespace-pre-line bg-orange-50/30 border border-orange-100/50 rounded-xl p-4">
                      {selectedRecipe.instructions}
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })()}
      </AnimatePresence>
    </div>
  );
};

export default Home;
